/**
 * HtmlCodeEditor — CodeMirror 6 HTML editor for Settings → Notifications
 * template body. Thin wrapper around @uiw/react-codemirror with:
 *   - HTML language (syntax highlight, bracket match, auto-indent)
 *   - Light/dark theme swap driven by ThemeContext
 *   - Imperative `insertAtCursor(text)` ref method used by the Insert-variable chips
 *
 * Out of scope: linting, autocomplete, vim/emacs keymaps. Kept intentionally minimal.
 */
import { forwardRef, useImperativeHandle, useRef } from 'react';
import CodeMirror from '@uiw/react-codemirror';
import { html } from '@codemirror/lang-html';
import { githubLight, githubDark } from '@uiw/codemirror-theme-github';
import { useTheme } from '../../contexts/ThemeContext';

const MIN_HEIGHT = '300px';
const MAX_HEIGHT = '600px';

const HtmlCodeEditor = forwardRef(function HtmlCodeEditor(
  { value, onChange, onFocus, testId = 'notif-body' },
  ref,
) {
  const { mode } = useTheme();
  const viewRef = useRef(null);

  useImperativeHandle(ref, () => ({
    /** Insert text at the current cursor position. Falls back to end-of-doc
     *  if the editor has never been focused. Mirrors the previous
     *  textarea.setSelectionRange semantics. */
    insertAtCursor(text) {
      const view = viewRef.current;
      if (!view) return;
      const head = view.state.selection.main.head ?? view.state.doc.length;
      view.dispatch({
        changes: { from: head, to: head, insert: text },
        selection: { anchor: head + text.length },
      });
      view.focus();
    },
    focus() { viewRef.current?.focus(); },
  }));

  return (
    <div
      // Match the Input / Textarea visual surface (same border-radius + focus ring)
      className="rounded-md border border-input bg-background overflow-hidden focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 focus-within:ring-offset-background transition-shadow"
      data-testid={`${testId}-wrapper`}
    >
      <CodeMirror
        value={value}
        onChange={onChange}
        onFocus={onFocus}
        theme={mode === 'dark' ? githubDark : githubLight}
        extensions={[html()]}
        minHeight={MIN_HEIGHT}
        maxHeight={MAX_HEIGHT}
        basicSetup={{
          lineNumbers: true,
          foldGutter: false,
          autocompletion: false,
          highlightActiveLine: true,
          bracketMatching: true,
          indentOnInput: true,
          searchKeymap: false,
          highlightSelectionMatches: false,
        }}
        onCreateEditor={(view) => { viewRef.current = view; }}
        data-testid={testId}
      />
    </div>
  );
});

export default HtmlCodeEditor;
